/** 학습내용 메모의 AI 학습계획. 키는 서버에만 둔다. */
import { supabase } from '../supabaseClient';
import { aiAccessErrorMessage, syncAiQuotaFromResponse } from '../services/aiQuotaService';
import { loadLearningPlanActivities, loadLearningPlanCategories } from './learningPlanActivityStore';

export type AiLearningPlanContext = {
  subject: string;
  grade: string;
  unit: string;
  learning: string;
  achievement: string;
  siblingLearnings: string[];
  request: string;
};

const ERRORS: Record<string, string> = {
  AI_NOT_CONFIGURED: 'AI 키가 설정되지 않아 계획을 만들지 못했습니다.',
  UNAUTHORIZED: '로그인이 필요합니다. 다시 로그인해 주세요.',
  RATE_LIMITED: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
  EMPTY_CONTEXT: '학습내용, 단원, 성취기준 중 하나는 있어야 계획을 만들 수 있습니다.',
  PLAN_TOO_VAGUE: '계획이 활동 이름에 그쳤습니다. 한 번 더 시도해 주세요.',
  PLAN_FORMAT: '계획 형식이 맞지 않습니다. 한 번 더 시도해 주세요.',
};

function apiUrl(): string {
  const base = (process.env.REACT_APP_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${base}/api/ai-learning-plan`;
}

export async function requestAiLearningPlan(input: AiLearningPlanContext): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error(ERRORS.UNAUTHORIZED);
  const categoryName = new Map(loadLearningPlanCategories().map((row) => [row.id, row.name]));
  const activities = loadLearningPlanActivities().map((row) => ({
    phase: row.phase,
    category: categoryName.get(row.phase) || '',
    name: row.name,
    method: row.method,
    guide: row.guide,
  }));
  let res: Response;
  try {
    res = await fetch(apiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...input, activities }),
    });
  } catch {
    throw new Error('AI 서버에 연결하지 못했습니다.');
  }
  const json = (await res.json().catch(() => null)) as { ok?: boolean; error?: string; memo?: unknown } | null;
  syncAiQuotaFromResponse(json);
  if (!json || !json.ok || typeof json.memo !== 'string' || !json.memo.trim()) {
    if (!json && res.status === 404) {
      throw new Error('AI 요청이 서버에 연결되지 않았습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.');
    }
    const code = json?.error || `HTTP_${res.status}`;
    throw new Error(aiAccessErrorMessage(code, json) ?? ERRORS[code] ?? '학습계획을 만들지 못했습니다. 다시 시도해 주세요.');
  }
  return json.memo.replace(/\r\n/g, '\n').trim();
}
